import { useRef } from 'react'
import Draggable, { DraggableEventHandler } from 'react-draggable'
import Paper, { PaperProps } from '@mui/material/Paper'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Theme } from '@mui/material/styles'

type Props = PaperProps & {
  position?: { x: number; y: number }
  onPositionChange?: (position: { x: number; y: number }) => void
}

export default function PaperComponent({ position, onPositionChange, ...props }: Props) {
  const nodeRef = useRef(null)
  const handleStop: DraggableEventHandler = (_, data) => {
    onPositionChange?.({ x: data.x, y: data.y })
  }
  // ⚠⚠ NO DRAG BELOW `sm`, AND NONE ON A TOUCH SCREEN (app#464). react-draggable takes a touch on
  // any part of the paper that is not in `cancel` (text, rows, padding): the finger moves the whole
  // popup, and the touch does not scroll. The Shared Access panel was seen on a phone with its
  // right edge and its close button off the screen. Below `sm` a popup is as wide as the screen,
  // so there is nowhere to move it to. A phone on its side and a tablet are wider than `sm`, so
  // the pointer is asked too.
  // ⚠ Below `sm` the position is also held at the origin, so a popup that was moved on a wide
  // window comes back when the window gets narrow.
  const narrow = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const touch = useMediaQuery('(pointer: coarse)')
  const fixed = narrow || touch
  return (
    <Draggable
      nodeRef={nodeRef}
      cancel=".MuiInputBase-input, pre, .xterm, .MuiButtonBase-root"
      disabled={fixed}
      position={narrow ? { x: 0, y: 0 } : position}
      onStop={onPositionChange ? handleStop : undefined}
    >
      {/* ⚠⚠ `move` ON THE PAPER, `auto` ON WHAT IT HOLDS. `cursor` INHERITS, so a single rule here
          painted the drag cursor over every descendant that does not set its own — including a
          DISABLED control, whose `pointer-events: none` lets the surface underneath answer for it.
          A disabled MUI Switch then reads exactly like the dialog's drag chrome, which is how a
          dead auto-refill switch went unexplained on the billing panel (Benoit, 2026-09-03: "the
          cursor is the move cursor everywhere"). Dragging from the body still works — the cursor
          simply stops claiming the body is nothing but a handle. */}
      <Paper
        ref={nodeRef}
        {...props}
        sx={{ ...props.sx, cursor: fixed ? 'auto' : 'move', '& > *': { cursor: 'auto' } }}
        onClick={e => e.stopPropagation()}
      />
    </Draggable>
  )
}
